<script>
import gql from 'graphql-tag'
import { mapGetters, mapState, mapMutations } from 'vuex'

import ItemsList from '@/components/checkout/ItemsList'
import PaymentMethod from '@/components/payment/PaymentMethod'
import Rating from '@/components/Rating'
import GoogleAddressAutocomplete from '@/components/GoogleAddressAutocomplete'
import googleMapsApi from '@/mixins/googleMapsApi'

import { DEFAULT_PAYMENT_METHOD_QUERY } from '@/graph/gql/payment.gql'
import { createOrder } from '@/graph/mutations/order'

import { openingHours, allowedHours } from '@/config'
const [start, end] = openingHours

const week = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']

export default {
  name: 'CheckoutView',
  components: { ItemsList, PaymentMethod, Rating, GoogleAddressAutocomplete },
  mixins: [googleMapsApi],
  props: {
    preloaded: { type: Object, default: () => null }
  },
  apollo: {
    defaultMethod: {
      query: DEFAULT_PAYMENT_METHOD_QUERY,
      fetchPolicy: 'cache-and-network',
      result({ data }) {
        if (!data || !data.defaultMethod) return

        if (!this.paymentMethod) {
          this.$store.commit('cart/setPaymentMethod', data.defaultMethod)
        }
      },
      skip() {
        return this.paymentMethod
      }
    },
    user: {
      query: gql`
        query {
          user: getViewer {
            id
            credits
          }
        }
      `,
      fetchPolicy: 'cache-and-network'
    }
  },
  data: () => ({
    prefillAddress: null,
    datePickerModal: false,
    submitting: false
  }),
  computed: {
    ...mapState({
      paymentMethod: state => state.cart.paymentMethod,
      chef: state => state.cart.chef,
      cartItems: state => state.cart.items
    }),
    ...mapGetters('cart', [
      'deliveryFee',
      'containerFeeSummary',
      'salesTax',
      'total'
    ]),

    availableDays() {
      const { omitBy, isEmpty } = this.lodash
      const { __typename, status, ...schedule } = this.chef.sellerSchedule

      return omitBy(schedule, isEmpty)
    },

    firstAvailableDay() {
      const { omitBy, max, keys, get } = this.lodash
      const { __typename, status, ...schedule } = this.chef.sellerSchedule

      const minOrderTime = this.$moment().add(24, 'hours')

      // Find first available day this week
      const weekday = week.reduce((acc, day) => {
        if (acc !== null) return acc

        const scheduleDay = get(this.availableDays, day, null)
        if (!scheduleDay) return acc

        const hour = max(scheduleDay)
        const date = this.$moment()
          .isoWeekday(day)
          .hour(hour)

        return minOrderTime.isBefore(date) ? date : null
      }, null)

      // If can't order this week, look at next week
      if (!weekday) {
        return week.reduce((acc, day, index) => {
          if (acc !== null) return acc

          const scheduleDay = get(this.availableDays, day, null)
          if (!scheduleDay) return acc

          return this.$moment().isoWeekday(index + 1 + 7)
        }, null)
      }

      return weekday
    },

    minDate() {
      return this.firstAvailableDay.format('YYYY-MM-DD')
    },

    maxDate() {
      return this.firstAvailableDay.add(2, 'week').format('YYYY-MM-DD')
    },

    deliveryAddress: {
      get() {
        return this.$store.state.cart.delivery.address
      },
      set(value) {
        this.setDeliveryAddress(value)
      }
    },

    deliveryDate: {
      get() {
        return this.$store.state.cart.delivery.date
      },
      set(value) {
        this.setDeliveryDate(value)
      }
    },

    deliveryHour: {
      get() {
        return this.$store.state.cart.delivery.hour
      },
      set(value) {
        this.setDeliveryHour(value)
      }
    },

    allowedHours() {
      const date = this.$moment(this.deliveryDate)
      const hour = date.hour()

      return date.isSame(this.firstAvailableDay, 'day')
        ? allowedHours.filter(x => x.value > hour)
        : allowedHours
    },

    formattedDeliveryDate() {
      return this.$moment(this.deliveryDate).calendar(null, {
        sameDay: '[Today]',
        nextDay: '[Tomorrow]',
        nextWeek: 'dddd Do',
        sameElse: 'dddd Do'
      })
    },

    formattedDeliveryHour() {
      const hour = this.$moment().hour(this.deliveryHour)
      const [t1, t2] = [hour.format('ha'), hour.add(1, 'hour').format('ha')]

      return `${t1}-${t2}`
    },

    credits() {
      if (!this.user) return 0
      const { credits } = this.user

      return this.lodash.clamp(credits, this.total)
    }
  },
  created() {
    if (!this.chef || !this.cartItems.length) {
      return this.$router.replace('/')
    }
    if (!this.cartItems.length) this.goBackToChef()

    this.prefillAddress = !this.deliveryAddress.name
  },
  mounted() {
    if (!this.deliveryAddress) {
      this.setDeliveryAddress({ name: '', place_id: '' })
    }

    if (!this.deliveryDate) {
      this.setDeliveryDate(this.firstAvailableDay.format('YYYY-MM-DD'))
    }

    if (!this.deliveryHour) {
      const { min } = this.lodash
      this.setDeliveryHour(min(this.allowedHours.map(x => x.value)))
    }
  },
  methods: {
    ...mapMutations('cart', [
      'setDeliveryAddress',
      'setDeliveryDate',
      'setDeliveryHour'
    ]),

    goBackToChef() {
      this.$router.replace({
        name: 'ChefView',
        params: { slug: this.chef.slug, preloaded: { chef: this.chef } }
      })
    },

    checkAllowedDate(date) {
      const { lowerCase } = this.lodash
      const day = lowerCase(this.$moment(date).format('ddd'))
      return !!this.availableDays[day]
    },

    goToPayment() {
      this.$router.push({
        name: 'PaymentView',
        params: {
          fromCheckout: true,
          navigation: { back: true, backTo: '/checkout' }
        }
      })
    },

    submit() {
      if (!this.paymentMethod) {
        return this.goToPayment()
      }

      if (this.submitting) return
      this.submitting = true

      const items = this.cartItems.map(({ id, quantity }) => ({
        productId: id,
        quantity
      }))

      const data = {
        sellerId: this.chef.id,
        items,
        paymentMethodId: this.paymentMethod.id,
        deliverTo: this.deliveryAddress,
        deliverOn: {
          date: this.deliveryDate,
          hour: this.deliveryHour
        }
      }

      createOrder(data)
        .then(() => {
          this.$router
            .replace({ name: 'MyOrdersView' })
            .then(() => this.$store.dispatch('cart/emptyCart'))
        })
        .finally(() => (this.submitting = false))
    }
  }
}
</script>

<template>
  <div class="checkout-view">
    <!-- Toolbar -->
    <v-app-bar color="white" flat app>
      <v-btn icon @click="goBackToChef">
        <v-icon color="black" size="18">$vuetify.icons.arrowBack</v-icon>
      </v-btn>
    </v-app-bar>

    <!-- Checkout view page -->
    <transition name="slide-up" appear>
      <div class="view-grid">
        <div class="content-top px-0">
          <div class="display-1 font-weight-bold mb-7 px-5">
            Checkout
          </div>

          <div>
            <!-- Checkout title -->
            <div v-if="chef" class="d-flex checkout-title px-5">
              <h4 class="flex-grow-1 subtitle-1 font-weight-bold">
                {{ chef.firstName }}
              </h4>
              <rating :rating="chef.sellerRating" />
            </div>

            <v-skeleton-loader
              v-if="!chef"
              class="mx-auto px-5"
              type="list-item"
              width="160px"
            />

            <!-- Items list -->
            <items-list :items="cartItems" />

            <v-divider class="my-3" />

            <!-- Fees -->
            <v-list class="px-5">
              <v-list-item v-if="deliveryFee" class="body-2">
                <span class="flex-grow-1"> Delivery fee </span>
                <div class="secondary--text">
                  {{ deliveryFee | round(2, true) }}
                </div>
              </v-list-item>

              <v-list-item v-if="containerFeeSummary" class="body-2">
                <span class="flex-grow-1"> Container fee </span>
                <div class="secondary--text">
                  {{ containerFeeSummary | round(2, true) }}
                </div>
              </v-list-item>

              <v-list-item v-if="salesTax" class="body-2">
                <span class="flex-grow-1"> VAT (%5) </span>
                <div class="secondary--text">
                  {{ salesTax | round(2, true) }}
                </div>
              </v-list-item>

              <v-list-item v-if="credits" class="body-2">
                <span class="flex-grow-1 accent--text"> Pona credits </span>
                <div class="accent--text">
                  &ndash; {{ credits | round(2, true) }}
                </div>
              </v-list-item>
            </v-list>

            <!-- Total -->
            <div class="d-flex mt-6 px-5">
              <h4 class="subtitle-1 font-weight-bold flex-grow-1">
                TOTAL
              </h4>
              <span class="subtitle-1 font-weight-regular primary--text">
                <!-- {{ currencyCode }} -->
                RM {{ (total - credits) | round(2, true) }}
              </span>
            </div>

            <div class="px-5">
              <v-skeleton-loader
                v-if="!paymentMethod && $apollo.loading"
                class="mx-auto"
                type="list-item-avatar-two-line"
              />

              <div v-if="paymentMethod" class="mt-8" @click="goToPayment">
                <payment-method :data="paymentMethod" checkout />
              </div>
            </div>

            <div class="d-flex px-5 mt-5">
              <v-dialog ref="dialog" v-model="datePickerModal">
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="formattedDeliveryDate"
                    label="Deliver on"
                    class="date-input"
                    prepend-inner-icon="$vuetify.icons.calendar"
                    color="black"
                    readonly
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="deliveryDate"
                  color="primary"
                  header-color="primary"
                  :show-current="false"
                  :allowed-dates="checkAllowedDate"
                  :min="minDate"
                  :max="maxDate"
                />
              </v-dialog>
              <v-select
                v-model="deliveryHour"
                :items="allowedHours"
                item-text="label"
                item-value="value"
                class="hour-input"
                prepend-inner-icon="$vuetify.icons.clock"
                color="black"
              />
            </div>

            <!-- Select location -->
            <div class="px-5">
              <GoogleAddressAutocomplete
                v-model="deliveryAddress"
                label="Deliver to"
                :prefill="prefillAddress"
                prepend-inner-icon="$vuetify.icons.compass"
              />
            </div>

            <div class="px-5">
              <v-btn
                :loading="submitting"
                rounded
                depressed
                color="primary"
                class="mt-4 px-6 mb-2"
                @click="submit"
              >
                Order for {{ formattedDeliveryDate }}
                {{ formattedDeliveryHour }}
              </v-btn>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<style lang="sass">
.checkout-view
  .content-top
    margin-top: 56px
  .checkout-title h4
    line-height: 21px
  .v-list-item
    font-size: 15px
    min-height: 30px
    padding: 0
  .address-autocomplete, .date-input, .hour-input
    .v-input__icon--prepend-inner .v-icon
      font-size: 16px
      color: black !important
    .v-label--active
      left: -28px !important
  .hour-input
    width: 140px
    .v-input__append-inner
      display: none
    .v-select__selections
      flex-wrap: nowrap
      input
        width: 1px
    .v-select__selection
      max-width: 100%
      width: 100%
</style>
